import { Link } from "react-router";
import { DataTable, type ColumnDef } from "@diametral/design-system/react";

import { useMyCoachees } from "../api/followUp";
import type { Coachee } from "../api/types";
import { Page } from "../components/Page";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/States";
import { useTranslation } from "../lib/i18n";

/** The people I coach (Boond `hrManager`): each opens on their year's follow-up. */
export function MyTeamPage() {
  const { t } = useTranslation();
  const coachees = useMyCoachees();

  const columns: ColumnDef<Coachee, unknown>[] = [
    {
      id: "name",
      accessorFn: (p) => p.name,
      header: t("Person"),
      cell: ({ row }) => (
        <span className="ada-person">
          <Link className="ada-link ada-person__name" to={`/people/${row.original.id}`}>
            {row.original.name}
          </Link>
          {row.original.email ? <span className="ada-person__title">{row.original.email}</span> : null}
        </span>
      ),
    },
    { accessorKey: "title", header: t("Job title") },
    {
      id: "projects",
      accessorFn: (p) => p.current_projects.join(", "),
      header: t("Current projects"),
      cell: ({ row }) =>
        row.original.current_projects.length ? (
          row.original.current_projects.join(", ")
        ) : (
          <span className="ada-faint">{t("No current project")}</span>
        ),
    },
  ];

  return (
    <Page
      kicker={t("My space")}
      title={t("My team")}
      description={t("The people you coach (HR manager in Boond): open one to follow their year, assignments and reports.")}
    >
      {coachees.isPending ? (
        <LoadingBlock lines={5} label={t("Loading your team…")} />
      ) : coachees.isError ? (
        <ErrorBlock error={coachees.error} onRetry={() => void coachees.refetch()} />
      ) : coachees.data.length ? (
        <div className="ada-table-wrap">
          <DataTable columns={columns} data={coachees.data} rowKey={(p) => String(p.id)} />
        </div>
      ) : (
        <EmptyBlock
          icon="users"
          title={t("Nobody to coach")}
          description={t("Boond names nobody with you as their HR manager.")}
        />
      )}
    </Page>
  );
}
